import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { FoodItem, FoodDatabaseStats, CustomFoodForm } from './types';
import { getDefaultUsdaFoods, getDefaultSpanishFoods } from './defaultFoods';
import { useCustomFoods } from '@/hooks/useCustomFoods';

export const useFoodSearch = (onAddFood?: (food: FoodItem, amount: number) => void) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [amount, setAmount] = useState<number>(100);
  const [allFoods, setAllFoods] = useState<FoodItem[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [dataLoadError, setDataLoadError] = useState<boolean>(false);
  const [lastSearched, setLastSearched] = useState<string[]>([]);
  const [offDataLoading, setOffDataLoading] = useState<boolean>(false);
  const [foodDatabaseStats, setFoodDatabaseStats] = useState<FoodDatabaseStats>({
    total: 0,
    usda: 0,
    openFoodFacts: 0
  });
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedResults, setPaginatedResults] = useState<FoodItem[]>([]);

  // Custom food creation
  const [showCustomFoodForm, setShowCustomFoodForm] = useState(false);
  const [customFood, setCustomFood] = useState<CustomFoodForm>({
    name: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });

  // Hook para alimentos personalizados
  const { customFoods, saveCustomFood, deleteCustomFood } = useCustomFoods();

  // Load data from local databases and combine with custom foods
  useEffect(() => {
    setLoading(true);
    
    const loadAllData = async () => {
      try {
        console.log('Loading food data from local database...');
        
        // Using only local data to avoid CORS errors
        const usdaData = getDefaultUsdaFoods();
        const offData = getDefaultSpanishFoods();
        
        // Combine all data sources including custom foods
        const combinedFoods = [...usdaData, ...offData, ...customFoods];
        
        // Update statistics
        setFoodDatabaseStats({
          total: combinedFoods.length,
          usda: usdaData.length,
          openFoodFacts: offData.length
        });
        
        setAllFoods(combinedFoods);
        setDataLoadError(false);
        
        const customCount = customFoods.length;
        const totalMessage = customCount > 0 
          ? `Base de datos de alimentos cargada: ${combinedFoods.length} alimentos (${customCount} personalizados)`
          : `Base de datos de alimentos cargada: ${combinedFoods.length} alimentos`;
        
        toast.success(totalMessage);
      } catch (error) {
        console.error('Error cargando bases de datos de alimentos:', error);
        setDataLoadError(true);
        
        // Fallback to default foods if there's an error
        const usdaFoods = getDefaultUsdaFoods();
        const spanishFoods = getDefaultSpanishFoods();
        const combinedFoods = [...usdaFoods, ...spanishFoods, ...customFoods];
        
        setAllFoods(combinedFoods);
        setFoodDatabaseStats({
          total: combinedFoods.length,
          usda: usdaFoods.length,
          openFoodFacts: spanishFoods.length
        });
        
        toast.info(`Usando base de datos local: ${combinedFoods.length} alimentos`);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
    
    // Load saved search terms from localStorage
    const savedSearches = localStorage.getItem('recentFoodSearches');
    if (savedSearches) {
      try {
        const searches = JSON.parse(savedSearches);
        setLastSearched(Array.isArray(searches) ? searches.slice(0, 5) : []);
      } catch (e) {
        console.error('Error parsing saved searches', e);
      }
    }
  }, [customFoods]); // Reload when custom foods change

  // Update pagination results when search results change
  useEffect(() => {
    if (searchResults.length > 0) {
      setTotalPages(Math.ceil(searchResults.length / resultsPerPage));
      const startIndex = (currentPage - 1) * resultsPerPage;
      const endIndex = startIndex + resultsPerPage;
      setPaginatedResults(searchResults.slice(startIndex, endIndex));
    } else {
      setPaginatedResults([]);
      setTotalPages(1);
    }
  }, [searchResults, currentPage, resultsPerPage]);

  // Function to save search terms
  const saveSearchTerm = (term: string) => {
    if (term.trim() === '') return;
    
    try {
      // Add to recent searches and remove duplicates
      const updatedSearches = [term, ...lastSearched.filter(s => s !== term)].slice(0, 5);
      setLastSearched(updatedSearches);
      localStorage.setItem('recentFoodSearches', JSON.stringify(updatedSearches));
    } catch (e) {
      console.error('Error saving search term', e);
    }
  };

  // Improved search function with better matching algorithm
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on new search
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (term.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    
    searchTimeoutRef.current = setTimeout(() => {
      console.log(`Searching for: "${term}" in unified food database (${allFoods.length} items)`);
      
      saveSearchTerm(term);
      
      // Prepare the search term (normalize and split)
      const normalizedTerm = term.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const searchTerms = normalizedTerm.split(/\s+/).filter(t => t.length > 1);
      
      // Improved search algorithm with better scoring
      const searchInDatabase = (foods: FoodItem[]): FoodItem[] => {
        // Return empty array if no foods or no valid search terms
        if (!foods.length || !searchTerms.length) return [];
        
        // Create a map to score each result
        const scoreMap = new Map<string, {food: FoodItem, score: number}>();
        
        for (const food of foods) {
          if (!food || !food.name) continue;
          
          // Normalize food name
          const normalizedFoodName = food.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const foodWords = normalizedFoodName.split(/\s+/);
          
          let score = 0;
          
          // 1. Exact complete match (highest score)
          if (normalizedFoodName === normalizedTerm) {
            score += 100;
          }
          
          // 2. Starts with exact term
          if (normalizedFoodName.startsWith(normalizedTerm)) {
            score += 80;
          }
          
          // 3. Contains exact term as substring
          if (normalizedFoodName.includes(normalizedTerm)) {
            score += 70;
          }
          
          // 4. Match individual words
          for (const term of searchTerms) {
            // Exact word match
            if (foodWords.includes(term)) {
              score += 60;
            }
            
            // Word starts with term
            const wordStartMatches = foodWords.filter(word => word.startsWith(term)).length;
            score += wordStartMatches * 40;
            
            // Word contains term
            const wordContainsMatches = foodWords.filter(word => word.includes(term)).length;
            score += wordContainsMatches * 30;
          }
          
          // 5. All search terms match somewhere in name
          const allTermsMatch = searchTerms.every(term => normalizedFoodName.includes(term));
          if (allTermsMatch) {
            score += 50;
          }
          
          // Boost score for custom foods to prioritize them
          if (food.source === 'Personalizado') {
            score += 10;
          }
          
          // Save score if relevant
          if (score > 0) {
            scoreMap.set(food.id, {food, score});
          }
        }
        
        // Convert map to array and sort by score
        return Array.from(scoreMap.values())
          .sort((a, b) => b.score - a.score) // Sort by score descending
          .map(item => item.food); // Extract food objects
      };
      
      // Search unified database
      const results = searchInDatabase(allFoods);
      console.log(`Found ${results.length} results for "${term}"`);
      
      // Set full results
      setSearchResults(results);
      setLoading(false);
    }, 300);
  };

  // Select a food
  const handleSelectFood = (food: FoodItem) => {
    setSelectedFood(food);
    setSearchResults([]);
    setSearchTerm('');
  };

  // Helper function to calculate nutrient value based on amount
  const calculateNutrientValue = (baseValue: number, quantity: number) => {
    return Math.round((baseValue * quantity / 100) * 10) / 10;
  };

  // Add selected food with corrected calculations
  const handleAddFood = () => {
    if (selectedFood && onAddFood) {
      console.log(`🍎 Adding food: ${selectedFood.name} - Amount: ${amount}g`);
      
      // Calculate nutritional values based on the actual amount
      const calculatedCalories = calculateNutrientValue(selectedFood.calories, amount);
      const calculatedProtein = calculateNutrientValue(selectedFood.protein, amount);
      const calculatedCarbs = calculateNutrientValue(selectedFood.carbs, amount);
      const calculatedFat = calculateNutrientValue(selectedFood.fat, amount);
      
      // Calculate optional nutrients if they exist
      const calculatedIron = selectedFood.iron ? calculateNutrientValue(selectedFood.iron, amount) : undefined;
      const calculatedCalcium = selectedFood.calcium ? calculateNutrientValue(selectedFood.calcium, amount) : undefined;
      const calculatedZinc = selectedFood.zinc ? calculateNutrientValue(selectedFood.zinc, amount) : undefined;
      const calculatedMagnesium = selectedFood.magnesium ? calculateNutrientValue(selectedFood.magnesium, amount) : undefined;
      const calculatedOmega3 = selectedFood.omega3 ? calculateNutrientValue(selectedFood.omega3, amount) : undefined;
      
      // Create food object with calculated values and quantity info
      const foodWithAmount = {
        ...selectedFood,
        // Override the nutritional values with calculated ones
        calories: calculatedCalories,
        protein: calculatedProtein,
        carbs: calculatedCarbs,
        fat: calculatedFat,
        iron: calculatedIron,
        calcium: calculatedCalcium,
        zinc: calculatedZinc,
        magnesium: calculatedMagnesium,
        omega3: calculatedOmega3,
        // Add quantity information
        quantity: amount,
        unit: 'g',
        displayQuantity: `${amount}g`
      };
      
      onAddFood(foodWithAmount, amount);
      setSelectedFood(null);
      setAmount(100);
      
      toast.success(`${selectedFood.name} añadido (${amount}g) - ${calculatedCalories} kcal`);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedFood(null);
  };

  // Change page
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle custom food form changes
  const handleCustomFoodChange = (field: keyof CustomFoodForm, value: string | number) => {
    setCustomFood(prev => ({ ...prev, [field]: value }));
  };

  // Add custom food - Updated to save to database
  const handleAddCustomFood = async () => {
    if (!customFood.name) {
      toast.error('Por favor, introduce un nombre para el alimento');
      return;
    }
    
    // Save to database
    const savedFood = await saveCustomFood({
      name: customFood.name,
      calories: customFood.calories,
      protein: customFood.protein,
      carbs: customFood.carbs,
      fat: customFood.fat
    });
    
    if (savedFood) {
      // Select the food
      setSelectedFood(savedFood);
      setShowCustomFoodForm(false);
      
      // Reset form
      setCustomFood({
        name: '',
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0
      });
      
      // Update stats will happen automatically via useEffect dependency on customFoods
    }
  };

  // Handle delete custom food
  const handleDeleteCustomFood = async (foodId: string) => {
    const success = await deleteCustomFood(foodId);
    if (success) {
      // Update search results if they contain the deleted food
      setSearchResults(prev => prev.filter(food => food.id !== foodId));
    }
  };

  return {
    searchTerm,
    searchResults,
    loading,
    selectedFood,
    amount,
    allFoods,
    dataLoadError,
    lastSearched,
    offDataLoading,
    foodDatabaseStats,
    currentPage,
    resultsPerPage,
    totalPages,
    paginatedResults,
    showCustomFoodForm,
    customFood,
    handleSearch,
    handleSelectFood,
    handleAddFood,
    handleClearSelection,
    handlePageChange,
    setShowCustomFoodForm,
    setAmount,
    handleCustomFoodChange,
    handleAddCustomFood,
    handleDeleteCustomFood
  };
};
